// ============================================================================
// XTRACKER MIDI PANEL
// Web MIDI API wrapper — device management, MIDI input → note entry,
// MIDI output → sequencer playback. Fails silently if browser lacks Web MIDI.
// ============================================================================

let midiAccess = null;
let selectedInput = null;
let selectedOutput = null;
let midiChannel = 1;          // 1-16, the MIDI channel we listen/send on
let recordEnabled = false;
let thruEnabled = true;
let activityCtx = null;
let activityCanvas = null;
let activityData = [];
let logEntries = [];
const MAX_LOG = 12;
const MAX_ACTIVITY = 200;

// Channel routing: maps tracker track index → { midiChannel, instrument, enabled }
let channelRouting = {};

// Callbacks into xtracker main module
let onNoteOn = null;    // (noteIndex, velocity, channel) → called on MIDI note on
let onNoteOff = null;   // (noteIndex, channel) → called on MIDI note off
let getTrackCount = null;
let getCurrentInstrumentIndex = null;

// ---- Note conversion: MIDI note number → FTNotes index ----
// MIDI note 0 = C-1, MIDI 60 = C-4, etc.
// FTNotes index: 1 = C-0, so midiNote + 1 = FTNotes index (roughly)
// Actually FTNotes: 1=C-0, 13=C-1, 25=C-2, 37=C-3, 49=C-4, 61=C-5
// MIDI: 0=C-1, 12=C-0... wait. MIDI 0 = C-1, MIDI 12 = C-0, MIDI 24 = C-1...
// FTNotes 1 = C-0 which is MIDI 12. So FTNotes index = midiNote - 11 = midiNote - 12 + 1
// FTNotes index = midiNote - 11
function midiToFTNote(midiNote) {
   return midiNote - 11;  // MIDI 60 (C-4) → FTNotes 49 (C-4) ✓
}

function ftNoteToMidi(ftIndex) {
   return ftIndex + 11;
}

// ---- Init: check Web MIDI support ----
function init(opts = {}) {
   onNoteOn = opts.onNoteOn || null;
   onNoteOff = opts.onNoteOff || null;
   getTrackCount = opts.getTrackCount || (() => 4);
   getCurrentInstrumentIndex = opts.getCurrentInstrumentIndex || (() => 1);

   activityCanvas = document.getElementById('xt-midi-activity');
   if (activityCanvas) activityCtx = activityCanvas.getContext('2d');

   if (!navigator.requestMIDIAccess) {
      setStatus('Web MIDI not supported in this browser');
      return false;
   }

   setStatus('Requesting MIDI access...');
   navigator.requestMIDIAccess({ sysex: false }).then(onMIDISuccess, onMIDIFailure);
   return true;
}

function onMIDISuccess(access) {
   midiAccess = access;
   setStatus('MIDI ready');
   refreshDeviceLists();
   midiAccess.onstatechange = () => refreshDeviceLists();
   startActivityLoop();
}

function onMIDIFailure(e) {
   setStatus('MIDI access denied: ' + (e.message || e));
}

function setStatus(msg) {
   const el = document.getElementById('xt-midi-status');
   if (el) el.textContent = msg;
}

// ---- Device lists ----
function refreshDeviceLists() {
   if (!midiAccess) return;
   const inputSel = document.getElementById('xt-midi-input');
   const outputSel = document.getElementById('xt-midi-output');
   if (!inputSel || !outputSel) return;

   const prevInput = selectedInput ? selectedInput.id : null;
   const prevOutput = selectedOutput ? selectedOutput.id : null;

   // Inputs
   inputSel.innerHTML = '<option value="">— none —</option>';
   const inputs = Array.from(midiAccess.inputs.values());
   inputs.forEach(inp => {
      const opt = document.createElement('option');
      opt.value = inp.id;
      opt.textContent = inp.name + (inp.manufacturer ? ' (' + inp.manufacturer + ')' : '');
      inputSel.appendChild(opt);
   });

   // Outputs
   outputSel.innerHTML = '<option value="">— none —</option>';
   const outputs = Array.from(midiAccess.outputs.values());
   outputs.forEach(out => {
      const opt = document.createElement('option');
      opt.value = out.id;
      opt.textContent = out.name + (out.manufacturer ? ' (' + out.manufacturer + ')' : '');
      outputSel.appendChild(opt);
   });

   // Restore selections
   if (prevInput) {
      inputSel.value = prevInput;
      selectInput(prevInput);
   }
   if (prevOutput) {
      outputSel.value = prevOutput;
      selectOutput(prevOutput);
   }

   inputSel.onchange = () => selectInput(inputSel.value);
   outputSel.onchange = () => selectOutput(outputSel.value);
}

function selectInput(id) {
   // Disconnect previous
   if (selectedInput && selectedInput.onmidimessage) {
      selectedInput.onmidimessage = null;
   }
   selectedInput = null;
   if (!id || !midiAccess) return;
   const inp = midiAccess.inputs.get(id);
   if (!inp) return;
   selectedInput = inp;
   inp.onmidimessage = handleMIDIMessage;
   setStatus('Input: ' + inp.name);
}

function selectOutput(id) {
   selectedOutput = null;
   if (!id || !midiAccess) return;
   const out = midiAccess.outputs.get(id);
   if (!out) return;
   selectedOutput = out;
   setStatus('Output: ' + out.name);
}

// ---- MIDI message handling ----
function handleMIDIMessage(e) {
   const data = e.data;
   if (!data || data.length < 2) return;

   const status = data[0] & 0xf0;
   const ch = (data[0] & 0x0f) + 1;  // 1-16

   // Record activity
   pushActivity(data);

   if (status === 0x90 && data[2] > 0) {
      // Note On
      const note = data[1];
      const vel = data[2];
      pushLog('ON  ch' + ch + ' note=' + note + ' vel=' + vel);
      // Thru: forward to output
      if (thruEnabled && selectedOutput) {
         selectedOutput.send([data[0], data[1], data[2]]);
      }
      // Route to xtracker note entry
      if (onNoteOn && (midiChannel === 0 || ch === midiChannel)) {
         const ftNote = midiToFTNote(note);
         onNoteOn(ftNote, vel, ch);
      }
   } else if (status === 0x80 || (status === 0x90 && data[2] === 0)) {
      // Note Off
      const note = data[1];
      pushLog('OFF ch' + ch + ' note=' + note);
      if (thruEnabled && selectedOutput) {
         selectedOutput.send([status === 0x80 ? data[0] : (0x80 | (ch - 1)), data[1], 0]);
      }
      if (onNoteOff && (midiChannel === 0 || ch === midiChannel)) {
         const ftNote = midiToFTNote(note);
         onNoteOff(ftNote, ch);
      }
   } else if (status === 0xb0) {
      // Control Change
      pushLog('CC  ch' + ch + ' cc=' + data[1] + ' val=' + data[2]);
      if (thruEnabled && selectedOutput) {
         selectedOutput.send([data[0], data[1], data[2]]);
      }
   } else if (status === 0xe0) {
      // Pitch Bend
      pushLog('PB  ch' + ch + ' ' + (data[2] << 7 | data[1]));
      if (thruEnabled && selectedOutput) {
         selectedOutput.send([data[0], data[1], data[2]]);
      }
   }
}

// ---- Send notes to MIDI output (for sequencer playback) ----
function sendNoteOn(midiChannel, note, velocity) {
   if (!selectedOutput) return;
   const ch = (midiChannel - 1) & 0x0f;
   selectedOutput.send([0x90 | ch, note & 0x7f, velocity & 0x7f]);
}

function sendNoteOff(midiChannel, note) {
   if (!selectedOutput) return;
   const ch = (midiChannel - 1) & 0x0f;
   selectedOutput.send([0x80 | ch, note & 0x7f, 0]);
}

function sendProgramChange(midiChannel, program) {
   if (!selectedOutput) return;
   const ch = (midiChannel - 1) & 0x0f;
   selectedOutput.send([0xc0 | ch, program & 0x7f]);
}

function sendControlChange(midiChannel, cc, value) {
   if (!selectedOutput) return;
   const ch = (midiChannel - 1) & 0x0f;
   selectedOutput.send([0xb0 | ch, cc & 0x7f, value & 0x7f]);
}

function sendAllNotesOff(midiChannel) {
   sendControlChange(midiChannel, 123, 0);
}

// ---- Channel routing UI ----
function renderRouting() {
   const container = document.getElementById('xt-midi-routing');
   if (!container) return;
   const tracks = getTrackCount();
   container.innerHTML = '';

   const table = document.createElement('div');
   table.className = 'xt-midi-routing-table';

   // Header
   const hdr = document.createElement('div');
   hdr.className = 'xt-midi-routing-row xt-midi-routing-hdr';
   hdr.innerHTML = '<span>TRK</span><span>ENABLE</span><span>MIDI CH</span><span>PROG</span><span>VEL</span>';
   table.appendChild(hdr);

   for (let t = 0; t < tracks; t++) {
      const r = channelRouting[t] || { enabled: false, midiCh: t + 1, program: 0, velocity: 100 };
      channelRouting[t] = r;

      const row = document.createElement('div');
      row.className = 'xt-midi-routing-row';

      const trackLabel = document.createElement('span');
      trackLabel.className = 'xt-midi-rt-track';
      trackLabel.textContent = String(t + 1).padStart(2, '0');
      row.appendChild(trackLabel);

      const enableLabel = document.createElement('label');
      enableLabel.className = 'xt-mb-check';
      const enableCb = document.createElement('input');
      enableCb.type = 'checkbox';
      enableCb.checked = r.enabled;
      enableCb.addEventListener('change', () => {
         r.enabled = enableCb.checked;
         if (r.enabled) {
            sendProgramChange(r.midiCh, r.program);
         }
      });
      enableLabel.appendChild(enableCb);
      row.appendChild(enableLabel);

      const chInput = document.createElement('input');
      chInput.type = 'number';
      chInput.min = 1; chInput.max = 16; chInput.step = 1;
      chInput.value = r.midiCh;
      chInput.className = 'xt-mb-num';
      chInput.style.width = '32px';
      chInput.addEventListener('change', () => {
         r.midiCh = Math.max(1, Math.min(16, parseInt(chInput.value, 10) || 1));
         chInput.value = r.midiCh;
         if (r.enabled) sendProgramChange(r.midiCh, r.program);
      });
      row.appendChild(chInput);

      const progInput = document.createElement('input');
      progInput.type = 'number';
      progInput.min = 0; progInput.max = 127; progInput.step = 1;
      progInput.value = r.program;
      progInput.className = 'xt-mb-num';
      progInput.style.width = '36px';
      progInput.addEventListener('change', () => {
         r.program = Math.max(0, Math.min(127, parseInt(progInput.value, 10) || 0));
         progInput.value = r.program;
         if (r.enabled) sendProgramChange(r.midiCh, r.program);
      });
      row.appendChild(progInput);

      const velInput = document.createElement('input');
      velInput.type = 'number';
      velInput.min = 0; velInput.max = 127; velInput.step = 1;
      velInput.value = r.velocity;
      velInput.className = 'xt-mb-num';
      velInput.style.width = '36px';
      velInput.addEventListener('change', () => {
         r.velocity = Math.max(0, Math.min(127, parseInt(velInput.value, 10) || 100));
         velInput.value = r.velocity;
      });
      row.appendChild(velInput);

      table.appendChild(row);
   }
   container.appendChild(table);
}

// ---- Sequencer: play a tracker note to MIDI output ----
function playTrackerNoteToMIDI(track, noteIndex, volume) {
   const r = channelRouting[track];
   if (!r || !r.enabled) return;
   const midiNote = ftNoteToMidi(noteIndex);
   const vel = Math.round((volume / 64) * r.velocity);
   sendNoteOn(r.midiCh, midiNote, vel);
}

function stopTrackerNoteToMIDI(track, noteIndex) {
   const r = channelRouting[track];
   if (!r || !r.enabled) return;
   const midiNote = ftNoteToMidi(noteIndex);
   sendNoteOff(r.midiCh, midiNote);
}

function stopAllMIDI() {
   for (let ch = 1; ch <= 16; ch++) sendAllNotesOff(ch);
}

// ---- Activity monitor ----
function pushActivity(data) {
   const now = performance.now();
   activityData.push({ t: now, data: data });
   if (activityData.length > MAX_ACTIVITY) activityData.shift();
}

function startActivityLoop() {
   function loop() {
      drawActivity();
      requestAnimationFrame(loop);
   }
   loop();
}

function drawActivity() {
   if (!activityCtx || !activityCanvas) return;
   const w = activityCanvas.clientWidth;
   const h = activityCanvas.clientHeight;
   const dpr = window.devicePixelRatio || 1;
   if (activityCanvas.width !== w * dpr) {
      activityCanvas.width = w * dpr;
      activityCanvas.height = h * dpr;
      activityCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
   }
   const tc = window.themeColors || [];
   activityCtx.fillStyle = tc[0] || '#0a0a0a';
   activityCtx.fillRect(0, 0, w, h);

   // Center line
   activityCtx.strokeStyle = tc[5] || '#333';
   activityCtx.lineWidth = 0.5;
   activityCtx.beginPath();
   activityCtx.moveTo(0, h / 2);
   activityCtx.lineTo(w, h / 2);
   activityCtx.stroke();

   const now = performance.now();
   const span = 3000; // show last 3 seconds
   activityData = activityData.filter(a => now - a.t < span);
   if (activityData.length === 0) return;

   activityCtx.strokeStyle = tc[15] || '#cccccc';
   activityCtx.lineWidth = 1;
   activityCtx.beginPath();
   let first = true;
   activityData.forEach(a => {
      const x = w - ((now - a.t) / span) * w;
      const status = a.data[0] & 0xf0;
      let y = h / 2;
      if (status === 0x90 && a.data[2] > 0) {
         y = h / 2 - (a.data[2] / 127) * (h / 2 - 2);
      } else if (status === 0x80 || (status === 0x90 && a.data[2] === 0)) {
         y = h / 2 + 4;
      } else if (status === 0xb0) {
         y = h / 2 - (a.data[2] / 127) * (h / 2 - 2) * 0.5;
      }
      if (first) { activityCtx.moveTo(x, y); first = false; }
      else activityCtx.lineTo(x, y);
   });
   activityCtx.stroke();
}

// ---- Event log ----
function pushLog(msg) {
   logEntries.unshift(msg);
   if (logEntries.length > MAX_LOG) logEntries.pop();
   const el = document.getElementById('xt-midi-log');
   if (!el) return;
   el.innerHTML = '';
   logEntries.forEach(m => {
      const line = document.createElement('div');
      line.className = 'xt-midi-log-line';
      line.textContent = m;
      el.appendChild(line);
   });
}

// ---- Wire UI controls ----
function wireControls() {
   const recCb = document.getElementById('xt-midi-record');
   const thruCb = document.getElementById('xt-midi-thru');
   const chInput = document.getElementById('xt-midi-channel');
   const refreshBtn = document.getElementById('xt-midi-refresh');
   if (recCb) recCb.addEventListener('change', () => { recordEnabled = recCb.checked; });
   if (thruCb) thruCb.addEventListener('change', () => { thruEnabled = thruCb.checked; });
   if (chInput) chInput.addEventListener('change', () => {
      midiChannel = Math.max(1, Math.min(16, parseInt(chInput.value, 10) || 1));
      chInput.value = midiChannel;
   });
   if (refreshBtn) refreshBtn.addEventListener('click', () => {
      if (!navigator.requestMIDIAccess) { setStatus('Web MIDI not supported'); return; }
      setStatus('Rescanning MIDI devices...');
      navigator.requestMIDIAccess({ sysex: false }).then((access) => {
         midiAccess = access;
         refreshDeviceLists();
         setStatus('MIDI devices refreshed');
      }, (e) => {
         setStatus('MIDI rescan failed: ' + (e.message || e));
      });
   });
}

// ---- Public API ----
const midiPanel = {
   init,
   wireControls,
   renderRouting,
   isSupported: () => !!navigator.requestMIDIAccess,
   isRecordEnabled: () => recordEnabled,
   playTrackerNoteToMIDI,
   stopTrackerNoteToMIDI,
   stopAllMIDI,
   sendNoteOn,
   sendNoteOff,
   sendProgramChange,
   sendControlChange,
   ftNoteToMidi,
   midiToFTNote,
};

export default midiPanel;
